import clsx from "clsx";
import React from "react";
import Account from "./Account";
import NetworkIndicator from "src/components/NetworkIndicator";
import TpvStatusPill from "src/realtime/TpvStatusPill";
import RecBadge from "./RecBadge";
import OnboardingWidget from "./OnboardingWidget";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import menu2Fill from "@iconify-icons/eva/menu-2-fill";
import clockIcon from "@iconify-icons/eva/clock-outline";
import { alpha, makeStyles, useTheme } from "@material-ui/core/styles";
import { Tooltip, useMediaQuery } from "@material-ui/core";
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  CardHeader,
  CardContent,
  Card,
  Typography,
} from "@material-ui/core";
// Button kept for the dialog confirm action
import AlertsCenter from "./AlertsCenter";
import IsRole from "src/utils/IsRole";
import { useSelector } from "src/redux/store";
import Roles from "src/utils/Roles";
import useEmployeeRole from "src/hooks/useEmployeeRole";
import ROLES_ENUM from "src/utils/enums";
import Label from "src/components/Label";
import { DialogAnimate } from "src/components/Animate";
import { fDateTime } from "src/utils/formatTime";
import InfoStrip from "../InfoStrip";
import { useDispatch } from "src/redux/store";
import { changeOpenClockingModal, getCurrentClockingSuccess, openKiosk } from "src/redux/slices/employee";
import { planFichar } from "src/utils/clocking/clockingFlow";
import { getSessionData } from "src/redux/slices/authJwt";
import { useLocation } from "react-router-dom";
import { PATH_APP } from "src/routes/paths";

function formatDuration(checkInTime) {
  const ms = Math.max(0, Date.now() - new Date(checkInTime + "Z").getTime());
  const totalSecs = Math.floor(ms / 1000);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function getShiftHours(checkInTime) {
  return (Date.now() - new Date(checkInTime + "Z").getTime()) / 3600000;
}

function getScheduledHours(clocking) {
  if (!clocking?.start || !clocking?.end) return 8;
  return Math.max(
    1,
    (new Date(clocking.end + "Z") - new Date(clocking.start + "Z")) / 3600000,
  );
}

function getActiveBreak(clocking) {
  const breaks = clocking?.currentShift?.breaks;
  if (!breaks) return null;
  return breaks.find((b) => b.started && !b.ended) ?? null;
}

function formatBreakDuration(breakStart) {
  if (!breakStart) return "--:--";
  const ms = Math.max(0, Date.now() - new Date(breakStart + "Z").getTime());
  const totalSecs = Math.floor(ms / 1000);
  const m = Math.floor(totalSecs / 60);
  const s = totalSecs % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

// ----------------------------------------------------------------------

const APPBAR_MOBILE = 34;
const APPBAR_DESKTOP = 52;

const useStyles = makeStyles((theme) => ({
  root: {
    boxShadow: "none",
    backdropFilter: "blur(8px)",
    backgroundColor: alpha(theme.palette.background.default, 0.72),
    [theme.breakpoints.up("lg")]: {},
  },
  toolbar: {
    minHeight: APPBAR_MOBILE,
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(0, 5),
    },
    [theme.breakpoints.up("lg")]: {
      minHeight: APPBAR_DESKTOP,
    },
  },
}));

// ----------------------------------------------------------------------

TopBar.propTypes = {
  onOpenNav: PropTypes.func,
  className: PropTypes.string,
};

function TopBar({ onOpenNav, className }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { claims, user, userSessions } = useSelector((state) => state.authJwt);
  const { clockings, employees, currentActiveEmployee } = useSelector(
    (state) => state.employees,
  );

  // Gate del botón "Fichar": si el restaurante activó
  // `restrictClockingToPrimaryDevice` Y este dispositivo NO es el
  // marcado como principal, ocultamos/deshabilitamos el botón para
  // que el empleado no reciba el 403 del backend al pulsar. Necesita
  // que la sesión actual esté en `userSessions` (fix del slice del
  // último turno — ver comentario allí).
  const currentSid = window.localStorage?.getItem("sessionIdentifier");
  const isCurrentDeviceClocking = React.useMemo(() => {
    if (!Array.isArray(userSessions) || userSessions.length === 0) return false;
    const currentSession = userSessions.find(
      (u) => u.userSessionIdentifier === currentSid,
    );
    return !!currentSession?.isClockingDevice;
  }, [userSessions, currentSid]);
  const clockingBlockedByDeviceRule =
    !!user?.restrictClockingToPrimaryDevice && !isCurrentDeviceClocking;

  // Hidratamos `userSessions` una sola vez al montar SI el restaurante
  // activó la operativa `restrictClockingToPrimaryDevice`. Sin este
  // trigger el gate de arriba nunca sabría si el device actual está
  // marcado. Ref anti-bucle (el fetch puede devolver el mismo array
  // por referencia distinta y disparar el effect otra vez).
  const sessionsHydrateRef = React.useRef(false);
  React.useEffect(() => {
    if (!user?.restrictClockingToPrimaryDevice) return;
    if (sessionsHydrateRef.current) return;
    if (Array.isArray(userSessions) && userSessions.length > 0) return;
    sessionsHydrateRef.current = true;
    dispatch(getSessionData());
  }, [user?.restrictClockingToPrimaryDevice, userSessions, dispatch]);
  const [open, setOpen] = React.useState(false);

  // Bloque "TakeMyOrder · NombreRestaurante" — sólo se monta en la vista
  // TPV principal. En el resto de rutas la información del restaurante ya
  // la cubre el InfoStrip central, así que evitamos pintarlo dos veces.
  const location = useLocation();
  const isTpvRoute = location.pathname === PATH_APP.tuCarta.tpv;
  const restaurantBrand = user?.nombreEmpresa || user?.firstName || null;
  const restaurantCity = user?.localidad || null;

  const hasEmployees = employees?.some((e) => e.isActive && !e.isAdmin);
  // Si el que está logueado es un empleado personal (login web con
  // email+password o QR personal), el botón "Fichar" NO debe abrir
  // el kiosko — fichar es acción propia, va directo al modal de
  // fichaje de ESE empleado. Ver comentario en useClaims: el rol
  // JWT no sirve porque hereda del restaurante padre; usamos la
  // flag explícita SessionKind.
  const isPersonalEmployeeSession = (() => {
    try { return window.localStorage.getItem("SessionKind") === "employee-web"; }
    catch { return false; }
  })();
  const needsKiosk =
    !isPersonalEmployeeSession &&
    (!currentActiveEmployee || currentActiveEmployee === 0);

  // 2026-10-07 · «Fichar» no cambia el empleado de la tablet:
  //  - con un empleado en la tablet → SU modal de fichaje, sin PIN (ya está
  //    identificado). Se fija explícitamente en él: el modal pudo quedarse
  //    apuntando a otro empleado que fichó antes desde el kiosko.
  //  - sin empleado (admin o kiosko) → kiosko en modo «fichar»: se elige quién
  //    ficha + PIN y la tablet sigue como estaba.
  const handleFichar = () => {
    const plan = planFichar({ needsKiosk, currentActiveEmployee });
    if (plan.type === "kiosk") {
      dispatch(openKiosk({ intent: "clocking" }));
      return;
    }
    if (plan.employeeId) dispatch(getCurrentClockingSuccess(plan.employeeId));
    dispatch(changeOpenClockingModal(true));
  };

  // Permisos del empleado activo (o "todos" si es el admin operando sin
  // empleado logueado). Filtran qué tabs del centro de alertas se
  // muestran — un camarero sin permiso de reservas no debería ver ni la
  // pestaña ni el badge de reservas pendientes.
  const { permissions } = useEmployeeRole();
  const canManageBookings = permissions.includes(ROLES_ENUM.RESTAURANT_BOOKING_MANAGER);
  const canManageEmployees = permissions.includes(ROLES_ENUM.RESTAURANT_EMPLOYEE_MANAGER);

  const activeClockings = clockings.filter(
    (u) => u.inProgress && u.currentShift,
  );

  const handleClose = () => {
    setOpen(false);
  };

  const showCurrentServices = () => {
    return (
      <DialogAnimate open={open} onClose={handleClose}>
        <DialogTitle>Fichajes activos</DialogTitle>

        <DialogContent>
          <Box>
            {activeClockings.map((clocking) => {
              const hours = getShiftHours(clocking.currentShift.checkInTime);
              const scheduled = getScheduledHours(clocking);
              const onBreak = clocking.currentShift?.breakInProgress;
              const shouldFinish = clocking.currentShift?.shouldServiceBeFinish;
              const activeBreak = getActiveBreak(clocking);
              const exceeded = hours >= scheduled;
              const exceededBadly = hours >= scheduled + 2;

              const labelColor =
                shouldFinish || exceededBadly
                  ? "error"
                  : exceeded
                    ? "warning"
                    : "info";

              const cardBorderColor = onBreak
                ? "#fa8c16"
                : shouldFinish || exceededBadly
                  ? "#ff4d4f"
                  : exceeded
                    ? "#faad14"
                    : "transparent";

              return (
                <Card
                  key={clocking.id}
                  sx={{
                    mb: 2,
                    p: 1,
                    borderLeft: `4px solid ${cardBorderColor}`,
                  }}
                >
                  <CardHeader
                    title={clocking.currentShift.employeeName}
                    subheader={
                      <Typography variant="caption" color="text.secondary">
                        Entrada: {fDateTime(clocking.currentShift.checkInTime)}
                      </Typography>
                    }
                  />
                  <CardContent sx={{ pt: 0 }}>
                    {/* Timer turno */}
                    <Label
                      color={onBreak ? "warning" : labelColor}
                      sx={{ fontSize: 14, fontFamily: "monospace" }}
                    >
                      {formatDuration(clocking.currentShift.checkInTime)}
                    </Label>

                    {/* Alerta turno excedido */}
                    {!onBreak && exceeded && (
                      <Typography
                        variant="caption"
                        color={`${labelColor}.main`}
                        sx={{ ml: 1 }}
                      >
                        {exceededBadly
                          ? `¡Turno excedido +${Math.floor(hours - scheduled)}h!`
                          : `Turno excedido (${Math.floor(scheduled)}h programadas)`}
                      </Typography>
                    )}

                    {/* Alerta debería haber salido */}
                    {!onBreak && shouldFinish && (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ ml: 1 }}
                      >
                        Debería haber salido
                      </Typography>
                    )}

                    {/* Bloque descanso activo */}
                    {onBreak && (
                      <Box
                        sx={{
                          mt: 1,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 1,
                          bgcolor: "#fff7e6",
                          border: "1px solid #fa8c1644",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "#d46b08", fontWeight: 600 }}
                        >
                          En descanso
                        </Typography>
                        {activeBreak?.breakStart && (
                          <Typography
                            sx={{
                              fontFamily: "monospace",
                              fontSize: 13,
                              color: "#d46b08",
                            }}
                          >
                            {formatBreakDuration(activeBreak.breakStart)}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {activeClockings === 0 && (
              <Typography color="text.secondary">
                No hay fichajes activos
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </DialogAnimate>
    );
  };

  return (
    <AppBar className={clsx(classes.root, className)}>
      {open && showCurrentServices()}
      <Toolbar className={classes.toolbar}>
        <IconButton
          onClick={onOpenNav}
          aria-label="Abrir menú"
          sx={{
            mr: 1,
            color: "text.primary",
          }}
        >
          <Icon icon={menu2Fill} />
        </IconButton>

        {isTpvRoute && restaurantBrand && (
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1,
              mr: 2,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.25,
                background:
                  "linear-gradient(135deg, #22c55e 0%, #facc15 100%)",
                color: "#08251a",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: "0.02em",
                flex: "0 0 auto",
              }}
            >
              T
            </Box>
            <Box sx={{ minWidth: 0, lineHeight: 1.15 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: 14,
                  letterSpacing: "0.01em",
                  color: "text.primary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title="TakeMyOrder"
              >
                TakeMyOrder
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 200,
                }}
                title={`${restaurantBrand}${restaurantCity ? ` · ${restaurantCity}` : ""}`}
              >
                {restaurantBrand}
                {restaurantCity ? ` · ${restaurantCity}` : ""}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ flexGrow: 1, mx: 1, minWidth: 0 }}>
          <InfoStrip onFichajesClick={() => setOpen(true)} />
        </Box>

        {/* Zona de acciones del TopBar. Simple flex + `gap` uniforme
            (idéntico entre todos los pares). Los componentes tienen
            anchos naturales distintos (IconButton ~30px, Button "Fichar"
            ~90px, Account chip ~180px) — el `gap` es la separación real
            entre BORDES, que es lo que percibe el ojo cuando los items
            son de tamaños dispares. Cualquier intento de "igualar
            centro-a-centro" con celdas fijas descoloca el layout y
            recorta el chip Account (probado y descartado). */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2, lg: 2.5 },
            "& .MuiIconButton-root": {
              width: { xs: 36, sm: "auto" },
              height: { xs: 36, sm: "auto" },
              padding: { xs: 0.5, sm: undefined },
            },
          }}
        >
          {/* Pastilla "● REC" parpadeante cuando la monitorización
              global está activa — transparencia para el usuario y
              acceso rápido al panel del monitor. */}
          <RecBadge />

          {/* Widget onboarding v2 — chip compacto con % progreso del
              wizard. Se auto-oculta si el user completó o pospuso hoy. */}
          <OnboardingWidget />

          {/* Indicador de calidad de red — visible siempre, a la izquierda
              de Notifications. Color del icono según estado; click abre
              popover con métricas. */}
          <NetworkIndicator />

          {/* Estado del TPV (tiempo real · worker de impresión · impresoras):
              una pastilla compacta en vez de bandas a todo lo ancho. No se
              ve si todo va bien; en móvil solo el punto de color. */}
          <TpvStatusPill variant="topbar" />

          {/* Centro de alertas unificado: una sola campana con tabs
              "Reservas" + "Fichajes" + "Hacienda" + "Correcciones" +
              "Avisos". El badge agrega los contadores y el color
              refleja la severidad más alta. Filtros por permisos del
              empleado activo. */}
          {!IsRole(claims.role, Roles.GLOBAL_ADMIN) &&
            !IsRole(claims.role, Roles.USER_DISTRIBUTOR) && (
              <AlertsCenter
                showBookings={canManageBookings}
                showSchedule={hasEmployees && canManageEmployees}
                showAeat
                showCorrections={hasEmployees && canManageEmployees}
              />
            )}

          {hasEmployees &&
            !IsRole(claims.role, Roles.GLOBAL_ADMIN) &&
            !IsRole(claims.role, Roles.USER_DISTRIBUTOR) && (
              <>
                {/* "Fichar" compacto: en mobile solo icono (reloj),
                    desktop icono + texto. Bloqueo por device rule
                    (Tooltip+span cuando disabled) mantiene el gap. */}
                {isMobile ? (
                  clockingBlockedByDeviceRule ? null : (
                    <Tooltip title="Fichar">
                      <IconButton
                        onClick={handleFichar}
                        sx={{ color: "primary.main" }}
                      >
                        <Icon icon={clockIcon} width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                  )
                ) : clockingBlockedByDeviceRule ? (
                  <Tooltip title="Este dispositivo no está autorizado para fichar. Usa el dispositivo principal (Cuenta → Sesiones).">
                    <span style={{ display: "inline-flex" }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Icon icon={clockIcon} width={16} height={16} />}
                        disabled
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Fichar
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Icon icon={clockIcon} width={16} height={16} />}
                    onClick={handleFichar}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Fichar
                  </Button>
                )}
              </>
            )}

          <Account />
        </Box>
      </Toolbar>
    </AppBar>
  );
}

export default TopBar;
